/**
 * Look a rule up: search by a word, or click a reference already on screen.
 *
 * Two entry points because she arrives from two directions. Working through her log she has a
 * reference in front of her and wants the rule behind it — that is `<HandbookRef>`, which turns
 * "CONC 5.2A" into a link without her doing anything. Writing something up she has a word and
 * wants to know where the rulebook deals with it — that is `<HandbookSearchBox>`.
 *
 * **Everything opens in a real window, not a tab**, which is the point: she puts it beside her
 * own work and reads the two together. A tab cannot be placed next to anything, and the only
 * thing that makes the difference is passing a width and height — see `openRule`. Every link
 * goes to the FCA's own page, which is always current, needs no sanitising from us, and is what
 * she would cite anyway.
 *
 * One thing that shapes the design: the FCA's Handbook site is an Angular SPA, so *every* path
 * returns 200. `…/handbook/nonsense99` is a healthy page showing nothing. So a reference is
 * never turned into a link on the client side — the server resolves it against a real tree
 * first, and an unresolved reference stays plain text rather than becoming a link that lies.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { api } from "../api";
import type { HandbookHit } from "../types";

/**
 * Open the rule in a real window, sized to sit beside this one.
 *
 * A **window**, not a tab, and that needs saying because the difference is entirely in the
 * features string: `window.open` with no sizing opens a tab, and a tab cannot be put next to
 * anything. Give it a width and a height and the browser makes an actual window she can place.
 *
 * It takes the right-hand half of the available screen and full height, which is the shape of
 * reading a rule against her own work. `availLeft`/`availTop` rather than 0 so it lands on the
 * screen she is using rather than the primary one.
 *
 * `noopener` is deliberately **not** passed, and that is the fix for a real bug rather than a
 * relaxation: with `noopener` set, browsers ignore a window name and treat every call as
 * `_blank` — so the named window was never reused and each lookup opened another tab. Dropping
 * it restores reuse, and `focus()` brings the existing window forward instead of quietly
 * updating one behind this one. The exposure it costs is `window.opener` on the opened page, and
 * that page is always `handbook.fca.org.uk` at a path built server-side from an id read out of
 * the FCA's own tree — never a URL from anything she or a model typed.
 */
function openRule(hit: HandbookHit) {
  // availLeft/availTop are non-standard and absent from lib.dom, but they are what makes this
  // land on the monitor she is using rather than always the primary one. Typed rather than cast
  // to any, so a browser without them falls back to 0 instead of NaN.
  const screen = window.screen as Screen & { availLeft?: number; availTop?: number };
  const width = Math.max(560, Math.floor(screen.availWidth / 2));
  const height = screen.availHeight;
  const left = (screen.availLeft ?? 0) + screen.availWidth - width;
  const top = screen.availTop ?? 0;

  const opened = window.open(
    hit.url,
    "fca-handbook",
    `popup=yes,width=${width},height=${height},left=${left},top=${top}` +
      ",scrollbars=yes,resizable=yes",
  );
  // Already open on a different rule: it has navigated, but it may be behind this window.
  opened?.focus();
}

/**
 * A reference in her prose, turned into a link if — and only if — it resolves.
 *
 * Resolution is server-side and cached per reference for the life of the page, because a log row
 * may mention the same chapter several times and the answer cannot change while she reads it.
 */
const resolved = new Map<string, HandbookHit | null>();

export function HandbookRef({ reference }: { reference: string }) {
  const [hit, setHit] = useState<HandbookHit | null | undefined>(resolved.get(reference));

  useEffect(() => {
    if (hit !== undefined) return;
    let live = true;
    api
      .handbookSearch({ ref: reference })
      .then((result) => {
        const found = result.items[0] ?? null;
        resolved.set(reference, found);
        if (live) setHit(found);
      })
      .catch(() => {
        resolved.set(reference, null);
        if (live) setHit(null);
      });
    return () => {
      live = false;
    };
  }, [reference, hit]);

  // Not a link until it is known to be one. An unresolved reference is still worth showing —
  // it is what she wrote — it just does not pretend to go anywhere.
  if (!hit) return <span className="ref-plain">{reference}</span>;

  return (
    <button
      className="ref-link"
      title={`${hit.name} — opens beside this window`}
      onClick={(event) => {
        event.stopPropagation();
        openRule(hit);
      }}
    >
      {reference} ↗
    </button>
  );
}

/**
 * Keyword search over Handbook chapter and section names.
 *
 * Names rather than full text, and the placeholder says so, because the honest limitation is
 * worth surfacing: "vulnerable customers" finds nothing not because the rulebook is silent on it
 * but because no chapter is *named* that. Better she knows that than concludes the search is
 * broken.
 */
export function HandbookSearchBox({
  autoFocus = false,
  placeholder = "Search the Handbook — “creditworthiness”, “CONC 5”, “complaints”",
}: {
  autoFocus?: boolean;
  placeholder?: string;
}) {
  const [query, setQuery] = useState("");
  const [hits, setHits] = useState<HandbookHit[] | null>(null);
  const [busy, setBusy] = useState(false);
  const timer = useRef<number | undefined>(undefined);

  const run = useCallback((text: string) => {
    if (text.trim().length < 2) {
      setHits(null);
      return;
    }
    setBusy(true);
    api
      .handbookSearch({ q: text })
      .then((result) => setHits(result.items))
      .catch(() => setHits([]))
      .finally(() => setBusy(false));
  }, []);

  // Debounced, because she types faster than a round trip and every keystroke is a request.
  useEffect(() => {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => run(query), 250);
    return () => window.clearTimeout(timer.current);
  }, [query, run]);

  return (
    <div className="handbook-lookup">
      <input
        className="handbook-query"
        value={query}
        autoFocus={autoFocus}
        placeholder={placeholder}
        onChange={(event) => setQuery(event.target.value)}
      />

      {busy && <span className="muted small"> searching…</span>}

      {hits !== null && hits.length === 0 && !busy && (
        <p className="small muted">
          No chapter or section is <em>named</em> that. The search reads Handbook headings, not
          rule text, so the rulebook may still cover it under another name — try a sourcebook
          (“CONC”, “DISP”) or a term the FCA would use in a heading.
        </p>
      )}

      {hits !== null && hits.length > 0 && (
        <ul className="handbook-hits">
          {hits.map((hit) => (
            <li key={hit.entity_id}>
              <button className="hit" onClick={() => openRule(hit)}>
                <code>{hit.reference}</code>
                <span className="hit-name">{hit.name}</span>
                <span className={`pill ${hit.type === "sourcebook" ? "" : "archived"}`}>
                  {hit.type}
                </span>
                <span className="muted small">↗</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
